/**
 * The Sentry vocabulary for the blog-subscribe -> HubSpot path, shared by the
 * browser form (`@dvd/blog/components/BlogSubscribeForm`) and the proxy route
 * (`@app/api/hubspot/submit`).
 *
 * It lives in `@lib` rather than beside the route because both sides need it and
 * neither may depend on the other: `@dvd` is the design system and `@app` is the
 * routes, so a shared module belongs on the layer they already share. It is not
 * in `@lib/constants` because none of this is a constant — it is a tag factory,
 * a checked union, and a lookup table.
 */

// Sentry `component` tag carried by every capture point on this path (client
// form, route, validation, rate limiter, reCAPTCHA), so
// `component:BlogSubscribeForm` selects the whole flow in one dashboard query.
//
// NOT `HubSpotForm`: `@dvd/components/HubSpotForm` — the embedded marketing form
// used by Cta/HeroForm/SplitForm, which shares no code with this path — already
// tags its own captures `component:HubSpotForm`. Reusing that value here would
// pull those unrelated embed failures into every query, where they would show up
// as an unattributable bucket carrying no `reason` tag at all.
export const BLOG_SUBSCRIBE_COMPONENT = 'BlogSubscribeForm';

/**
 * Sentry `reason` tag: which capture point on the path fired. Tags are indexed by
 * Sentry and `extra` is not, so anything we want to filter, group, or chart on
 * has to live here — `extra` is only for reading an individual event.
 *
 * This union is the canonical list the Sentry dashboard is built from, which is
 * why it is a type and not a loose string: a mistyped tag would silently drop an
 * originator from the dashboard, and silence is exactly the failure mode we are
 * fixing. A typo is a `yarn tsc` error instead.
 *
 * `reason` does not drive Sentry's issue grouping — `captureMessage` fingerprints
 * on the message *text*, so two capture points sharing a message are one issue,
 * one alert, and one resolve button no matter how they are tagged. Every capture
 * point on this path therefore sends a message unique to its reason; `reason`
 * exists to filter and chart, not to separate issues.
 */
export type BlogSubscribeReason =
  // validation.ts — request never reached the route body
  | 'oversized-content-length'
  | 'oversized-body'
  | 'invalid-json'
  | 'schema-mismatch'
  // rate-limit.ts — `saturation` is the attack signal, distinct from one IP
  // merely exhausting its quota (a NAT'd office can do the latter innocently)
  | 'rate-limit-ip-quota'
  | 'rate-limit-saturation'
  // route.ts
  | 'formid-not-allowed'
  | 'hubspot-unreachable'
  | 'hubspot-rejected'
  | 'route-unhandled'
  // recaptcha.ts
  | 'recaptcha-missing-token'
  | 'recaptcha-verify-unreachable'
  | 'recaptcha-rejected'
  // BlogSubscribeForm.tsx — the browser side. `recaptcha-script-failed` is the
  // one failure the server can never see: the script never loads, so the submit
  // button stays disabled and no request is ever made. The three submit reasons
  // are split by status because they mean opposite things operationally — a 429
  // is the rate limiter working, a 5xx is an outage — and collapsing them hides
  // a real incident inside expected noise.
  | 'recaptcha-script-failed'
  | 'client-rate-limited'
  | 'client-rejected'
  | 'client-submit-failed';

/** Tags for any Sentry capture on the blog-subscribe -> HubSpot path. */
export const blogSubscribeTags = (reason: BlogSubscribeReason) => ({
  component: BLOG_SUBSCRIBE_COMPONENT,
  reason,
});

/**
 * The statuses the proxy route returns *on purpose* — its rejection contract,
 * stated once so the browser and the route cannot drift apart on what counts as
 * a fault.
 *
 * Every entry is a guard doing its job, not an incident: `429` is the rate
 * limiter engaging, `400` is validation refusing a malformed body, `403` is the
 * reCAPTCHA gate refusing a bot. The client reports these as `warning` messages.
 *
 * Anything absent falls through to `captureException` — `5xx` (HubSpot is
 * unreachable, or the route threw), a network drop, or a `grecaptcha` throw, all
 * of which are real faults that must page. Without the split, a HubSpot outage
 * arrives at the same level, and in the same Sentry issue, as routine bot
 * rejections; and the route's careful one-warning-per-IP-per-window throttle is
 * undone by an unthrottled client event for every single blocked request.
 *
 * Keyed by status because the browser already has it — nothing here is
 * privileged information. `400` and `403` deliberately share a reason: the route
 * answers both with a vague body precisely so a prober cannot tell which check
 * tripped, so the client genuinely cannot distinguish them either.
 */
export const CLIENT_EXPECTED_REJECTIONS: Record<
  number,
  { reason: BlogSubscribeReason; message: string }
> = {
  429: {
    reason: 'client-rate-limited',
    message: 'Blog subscribe submission rate limited',
  },
  400: {
    reason: 'client-rejected',
    message: 'Blog subscribe submission rejected by proxy',
  },
  403: {
    reason: 'client-rejected',
    message: 'Blog subscribe submission rejected by proxy',
  },
};
